import React, { useCallback, useEffect, useState } from 'react';
import { articulosApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Boxes, RefreshCw, Pencil, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const TAB_TODOS = 'todos';
const TAB_PENDIENTES = 'pendientes';

const CLASIFICADO_OPTS = [
  { value: '__all__', label: 'Todos' },
  { value: 'auto',   label: 'Clasificado automáticamente' },
  { value: 'manual', label: 'Revisado manualmente' },
];

function BadgeClasificado({ auto }) {
  return auto
    ? <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-ink-100 text-ink-500">Auto</span>
    : <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-brand-100 text-brand-700 font-medium">Manual</span>;
}

function SubfamiliaSelect({ familias, familiaId, value, onChange }) {
  const subs = familiaId != null
    ? (familias.find((f) => f.id === Number(familiaId))?.subfamilias || [])
    : [];
  return (
    <Select value={value ?? '__none__'} onValueChange={(v) => onChange(v === '__none__' ? null : v)} disabled={familiaId == null}>
      <SelectTrigger className="w-56 h-8 text-xs"><SelectValue placeholder="Subfamilia…" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">Todas las subfamilias</SelectItem>
        {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.id} — {s.nombre}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Articulos() {
  const { user } = useAuth();
  const canWrite = ['admin', 'admin_comercial', 'dir_comercial'].includes(user?.rol);

  const [tab, setTab] = useState(TAB_TODOS);
  const [familias, setFamilias] = useState([]);

  // Filtros
  const [q, setQ] = useState('');
  const [familiaFilter, setFamiliaFilter] = useState(null);
  const [subfamiliaFilter, setSubfamiliaFilter] = useState(null);
  const [clasificadoFilter, setClasificadoFilter] = useState('__all__');

  // Datos
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const LIMIT = 50;

  // Selección masiva
  const [selected, setSelected] = useState(new Set());

  // Edición inline
  const [editCodigo, setEditCodigo]   = useState(null);
  const [editFamilia, setEditFamilia] = useState(null);
  const [editSub, setEditSub]         = useState(null);

  // Modal reclasificación masiva
  const [modal, setModal]         = useState({ open: false });
  const [modalFamilia, setModalFamilia] = useState(null);
  const [modalSub, setModalSub]   = useState(null);
  const [saving, setSaving]       = useState(false);

  // Clasificar desde documentos
  const [clasificando, setClasificando] = useState(false);

  // ── Carga inicial de familias ──────────────────────────────────
  useEffect(() => {
    articulosApi.familias().then(setFamilias).catch(() => {});
  }, []);

  // ── Carga de artículos ─────────────────────────────────────────
  const load = useCallback(async (p = 1) => {
    const params = {
      page: p,
      limit: LIMIT,
      pendiente_revision: tab === TAB_PENDIENTES || undefined,
    };
    if (tab === TAB_TODOS) {
      if (q) params.descripcion = q;
      if (familiaFilter != null) params.familia_id = familiaFilter;
      if (subfamiliaFilter) params.subfamilia_id = subfamiliaFilter;
      if (clasificadoFilter === 'auto') params.clasificado_auto = true;
      if (clasificadoFilter === 'manual') params.clasificado_auto = false;
    }
    try {
      const res = await articulosApi.list(params);
      setItems(res.items);
      setTotal(res.total);
      setPage(p);
      setSelected(new Set());
    } catch (e) {
      toast.error('Error cargando artículos');
    }
  }, [tab, q, familiaFilter, subfamiliaFilter, clasificadoFilter]);

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [tab, q, familiaFilter, subfamiliaFilter, clasificadoFilter]);

  // ── Cambio de tab ──────────────────────────────────────────────
  const handleTab = (t) => {
    setTab(t);
    setQ('');
    setFamiliaFilter(null);
    setSubfamiliaFilter(null);
    setClasificadoFilter('__all__');
    setSelected(new Set());
    setEditCodigo(null);
  };

  // ── Checkbox individual ────────────────────────────────────────
  const toggleRow = (codigo) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(codigo) ? next.delete(codigo) : next.add(codigo);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((a) => a.codigo)));
    }
  };

  // "Seleccionar todos los filtrados" — llama a la API sin paginación
  const selectAllFiltered = async () => {
    try {
      const params = { limit: 10000, pendiente_revision: tab === TAB_PENDIENTES || undefined };
      if (tab === TAB_TODOS) {
        if (q) params.descripcion = q;
        if (familiaFilter != null) params.familia_id = familiaFilter;
        if (subfamiliaFilter) params.subfamilia_id = subfamiliaFilter;
        if (clasificadoFilter === 'auto') params.clasificado_auto = true;
        if (clasificadoFilter === 'manual') params.clasificado_auto = false;
      }
      const res = await articulosApi.list(params);
      setSelected(new Set(res.items.map((a) => a.codigo)));
      toast.success(`${res.items.length} artículos seleccionados`);
    } catch {
      toast.error('No se pudieron seleccionar todos');
    }
  };

  // ── Edición inline ─────────────────────────────────────────────
  const startEdit = (art) => {
    setEditCodigo(art.codigo);
    setEditFamilia(art.familia_id);
    setEditSub(art.subfamilia_id);
  };

  const cancelEdit = () => { setEditCodigo(null); setEditFamilia(null); setEditSub(null); };

  const saveEdit = async () => {
    if (editFamilia == null || !editSub) { toast.error('Selecciona familia y subfamilia'); return; }
    try {
      await articulosApi.update(editCodigo, { familia_id: Number(editFamilia), subfamilia_id: editSub });
      toast.success('Artículo reclasificado');
      cancelEdit();
      load(page);
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    }
  };

  // ── Reclasificación masiva ─────────────────────────────────────
  const openModal = () => {
    if (!selected.size) { toast.error('Selecciona al menos un artículo'); return; }
    setModalFamilia(null);
    setModalSub(null);
    setModal({ open: true });
  };

  const applyMasivo = async () => {
    if (modalFamilia == null || !modalSub) { toast.error('Selecciona familia y subfamilia'); return; }
    setSaving(true);
    try {
      const res = await articulosApi.clasificarMasivo({
        codigos: [...selected],
        familia_id: Number(modalFamilia),
        subfamilia_id: modalSub,
      });
      toast.success(`${res.articulos_actualizados} artículos reclasificados`);
      setModal({ open: false });
      setSelected(new Set());
      load(page);
    } catch (e) {
      toast.error(e.message || 'Error en reclasificación');
    } finally {
      setSaving(false);
    }
  };

  // ── Clasificar desde documentos ────────────────────────────────
  const clasificarDesdeDocumentos = async () => {
    if (!window.confirm('Esto clasificará todos los artículos extraídos de los documentos ERP importados. ¿Continuar?')) return;
    setClasificando(true);
    try {
      const res = await articulosApi.clasificarDesdeDocumentos();
      toast.success(`${res.total} artículos procesados — ${res.clasificados} clasificados, ${res.sin_clasificar} sin clasificar`);
      load(1);
    } catch (e) {
      toast.error(e.message || 'Error al clasificar');
    } finally {
      setClasificando(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  // ── Familia filter reset subfamilia cuando cambia ──────────────
  const handleFamiliaFilter = (v) => {
    setFamiliaFilter(v === '__all__' ? null : Number(v));
    setSubfamiliaFilter(null);
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="articulos-page">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight flex items-center gap-2">
            <Boxes className="w-7 h-7 text-brand-600" /> Artículos ERP
          </h1>
          <p className="text-sm text-ink-500 mt-1">Clasificación de artículos por familias y subfamilias de producto</p>
        </div>
        {canWrite && (
          <Button
            variant="outline"
            onClick={clasificarDesdeDocumentos}
            disabled={clasificando}
            data-testid="btn-clasificar-documentos"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${clasificando ? 'animate-spin' : ''}`} />
            {clasificando ? 'Clasificando…' : 'Clasificar desde documentos'}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-ink-200">
        {[
          { id: TAB_TODOS,      label: 'Todos' },
          { id: TAB_PENDIENTES, label: 'Pendientes de revisión' },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => handleTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-ink-500 hover:text-ink-900'
            }`}
            data-testid={`tab-${id}`}
          >
            {label}
            {id === TAB_PENDIENTES && total > 0 && tab === TAB_PENDIENTES && (
              <span className="ml-2 bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">{total}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filtros — solo en tab Todos */}
      {tab === TAB_TODOS && (
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            placeholder="Buscar código o descripción…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-64"
            data-testid="input-buscar-articulos"
          />
          <Select value={familiaFilter != null ? String(familiaFilter) : '__all__'} onValueChange={handleFamiliaFilter}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Familia" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas las familias</SelectItem>
              {familias.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.id} — {f.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          <SubfamiliaSelect
            familias={familias}
            familiaId={familiaFilter}
            value={subfamiliaFilter}
            onChange={setSubfamiliaFilter}
          />
          <Select value={clasificadoFilter} onValueChange={setClasificadoFilter}>
            <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CLASIFICADO_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Barra de selección masiva */}
      {canWrite && (
        <div className="flex items-center gap-3 flex-wrap">
          {selected.size > 0 && (
            <>
              <span className="text-sm font-medium text-brand-700 bg-brand-50 px-3 py-1 rounded-full">
                {selected.size} artículos seleccionados
              </span>
              <Button size="sm" onClick={openModal} data-testid="btn-reclasificar-masivo">
                Reclasificar seleccionados
              </Button>
              <button className="text-xs text-ink-500 hover:text-ink-700 underline" onClick={() => setSelected(new Set())}>
                Limpiar selección
              </button>
            </>
          )}
          {items.length > 0 && total > LIMIT && (
            <button
              className="text-xs text-ink-500 hover:text-brand-600 underline"
              onClick={selectAllFiltered}
              data-testid="btn-seleccionar-todos"
            >
              Seleccionar los {total} filtrados
            </button>
          )}
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-ink-600 uppercase text-xs">
              <tr>
                {canWrite && (
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selected.size === items.length}
                      onChange={toggleAll}
                      className="rounded"
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Descripción</th>
                <th className="text-left px-4 py-3">Familia</th>
                <th className="text-left px-4 py-3">Subfamilia</th>
                <th className="text-center px-4 py-3">Clasificado</th>
                {canWrite && <th className="px-4 py-3 w-20"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {items.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 7 : 5} className="px-4 py-12 text-center text-ink-400">
                    {tab === TAB_PENDIENTES ? 'No hay artículos pendientes de revisión' : 'Sin artículos. Usa "Clasificar desde documentos" para poblar el catálogo.'}
                  </td>
                </tr>
              )}
              {items.map((art) => {
                const isEditing = editCodigo === art.codigo;
                return (
                  <tr key={art.codigo} className={`hover:bg-ink-50 ${selected.has(art.codigo) ? 'bg-brand-50/40' : ''}`} data-testid={`art-row-${art.codigo}`}>
                    {canWrite && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(art.codigo)}
                          onChange={() => toggleRow(art.codigo)}
                          className="rounded"
                          disabled={isEditing}
                        />
                      </td>
                    )}
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-ink-900 whitespace-nowrap">{art.codigo}</td>
                    <td className="px-4 py-2 text-ink-700 max-w-xs truncate" title={art.descripcion}>{art.descripcion || '—'}</td>

                    {isEditing ? (
                      <>
                        <td className="px-4 py-2" colSpan={2}>
                          <div className="flex gap-2 items-center flex-wrap">
                            <Select
                              value={editFamilia != null ? String(editFamilia) : '__none__'}
                              onValueChange={(v) => { setEditFamilia(v === '__none__' ? null : Number(v)); setEditSub(null); }}
                            >
                              <SelectTrigger className="w-52 h-8 text-xs"><SelectValue placeholder="Familia…" /></SelectTrigger>
                              <SelectContent>
                                {familias.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.id} — {f.nombre}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <SubfamiliaSelect familias={familias} familiaId={editFamilia} value={editSub} onChange={setEditSub} />
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2 text-ink-600 text-xs">{art.familia_id} — {art.familia_nombre}</td>
                        <td className="px-4 py-2 text-ink-500 text-xs">{art.subfamilia_id} — {art.subfamilia_nombre}</td>
                      </>
                    )}

                    <td className="px-4 py-2 text-center">
                      <BadgeClasificado auto={art.clasificado_auto} />
                    </td>

                    {canWrite && (
                      <td className="px-4 py-2 text-right">
                        {isEditing ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" onClick={saveEdit} className="text-green-600 hover:text-green-700 p-1" title="Guardar">
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-ink-400 hover:text-ink-600 p-1" title="Cancelar">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => startEdit(art)} className="p-1" title="Editar clasificación">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-ink-500">
            {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} de {total} artículos
          </p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => load(page - 1)} disabled={page === 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-3 py-1 text-sm text-ink-600">Pág. {page} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => load(page + 1)} disabled={page === totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal reclasificación masiva */}
      <Dialog open={modal.open} onOpenChange={(v) => setModal({ open: v })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reclasificar {selected.size} artículos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1.5 block">Familia</Label>
              <Select
                value={modalFamilia != null ? String(modalFamilia) : '__none__'}
                onValueChange={(v) => { setModalFamilia(v === '__none__' ? null : Number(v)); setModalSub(null); }}
              >
                <SelectTrigger className="w-full"><SelectValue placeholder="Selecciona una familia…" /></SelectTrigger>
                <SelectContent>
                  {familias.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.id} — {f.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Subfamilia</Label>
              <SubfamiliaSelect familias={familias} familiaId={modalFamilia} value={modalSub} onChange={setModalSub} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false })}>Cancelar</Button>
            <Button onClick={applyMasivo} disabled={saving || modalFamilia == null || !modalSub}>
              {saving ? 'Aplicando…' : `Aplicar a ${selected.size} artículos`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
